import {
  type ImportComputation,
  type ReimportCandidate,
  SHEET_TYPES,
  type SheetType,
  type StructurePrefill,
  isSameFileStem,
} from '@testflow/shared';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SavedHint } from '../../components/saved-hint.js';
import {
  type ImportJob,
  type ImportSheet,
  type StructureSaved,
  type UploadResult,
  importApi,
} from '../../lib/import-api.js';
import { useSavedFlash } from '../../lib/use-saved-flash.js';
import { ComputationStatus } from './computation-status.js';
import { translateImportError } from './import-errors.js';
import { useImportComputation } from './use-import-computation.js';

// Design-Spec 3.8: Datei-Kopf mit Blatt-Tabs und KI-Typ je Blatt, Chip
// "✦ KI: Schritt-Detail · Konfidenz 96%" (IMP-2). Jeder KI-Befund ist überschreibbar (HITL).
//
// translateImportError lebt in import-errors.ts (Task 19: vom Mapping-Schritt mitgenutzt).

// Task 8 (#64): nach dem Upload zeigt der Wizard die Struktur-Vorbelegung — editierbar, je Feld
// unabhängig auf Blur persistiert (PATCH .../structure). Erst der explizite Weiter-Klick gibt den
// Job an den Wizard-Container weiter (HITL: die Struktur soll geprüft/korrigiert werden können,
// bevor die Klassifikation beginnt).
//
// #112 (A10): WELCHES Feld gezeigt wird, entscheidet die Blattart — nicht mehr beide immer.
//  — Ein `scenario_flow`-Blatt erzeugt Ketten je `scenario_id`; nur dann ist ein STREAM gemeint,
//    und nur dann legt der Commit überhaupt einen Stream-Knoten an. Ein Stream-Feld ohne
//    scenario_flow-Blatt wäre ein Eingabefeld ohne jede Wirkung.
//  — Alle anderen Blattarten sind Prozess-/Testfall-Blätter: der Dateiname benennt das
//    TEILPROJEKT. Vorher wurde `teilprojekt_name` gar nicht vorbelegt und blieb in der Praxis
//    leer — die Ursache dafür, dass importierte Prozesse unzugeordnet wirkten.
interface UploadedState {
  jobId: string;
  // #152: Dateiname des Uploads — Grundlage des Vergleichs mit dem Dateinamen der gewählten Quelle.
  filename: string;
  sheets: ImportSheet[];
  streamName: string;
  teilprojektName: string;
  // #125 (Q5): frühere Importe des Kunden als mögliche Quelle eines Neuimports; leer = keine
  // Auswahl sichtbar (es gibt nichts abzugleichen).
  candidates: ReimportCandidate[];
  reimportOf: string; // '' = Neuanlage; sonst die GESPEICHERTE Quelle (nie ein unbestätigter Wunsch)
  suggested: boolean; // der Server hat eine Quelle vorgeschlagen (nur dann der „Vorgeschlagen…"-Satz)
  // Folgeauftrag #125: welche Struktur-Felder aus der Quelle übernommen sind. Eine eigene Eingabe
  // nimmt das Feld heraus (der Hinweis stimmt dann nicht mehr).
  structurePrefill: StructurePrefill | null;
  // Fix-Runde 1 (#125): der zuletzt gespeicherte Stand der Struktur-Felder. Ein Blur ohne Änderung
  // schickt nichts — sonst ginge ein sichtbares, leeres Stream-Feld als `stream_name: ''` raus.
  savedStream: string;
  savedTeilprojekt: string;
  // Fix-Runde 2 (#125): Felder, die der Admin in diesem Schritt selbst bearbeitet hat. Gehen bei
  // jeder Quellwahl mit — der Server übernimmt sie dann nicht (auch wenn der Wert zufällig dem
  // eines anderen Imports gleicht, etwa ein geleertes Teilprojekt).
  edited: Array<'stream_name' | 'teilprojekt_name'>;
}

// #125: Kandidaten je Import-Familie. Eine Familie mit mehreren Importen wird zur Gruppe (Titel =
// ältester Import, Reihenfolge wie geliefert: neueste zuerst) — sonst stünde ein abgelöster Altjob
// mit 0 Testfällen da wie ein leerer, fremder Import. Einzelne Importe bleiben ungruppiert.
function groupByFamily(
  candidates: ReimportCandidate[],
): Array<{ family: ReimportCandidate | null; members: ReimportCandidate[] }> {
  const groups = new Map<string, ReimportCandidate[]>();
  for (const c of candidates) {
    const list = groups.get(c.family_id);
    if (list) list.push(c);
    else groups.set(c.family_id, [c]);
  }
  return [...groups.entries()].map(([familyId, members]) => ({
    family:
      members.length > 1
        ? (members.find((m) => m.import_job_id === familyId) ?? members.at(-1) ?? null)
        : null,
    members,
  }));
}

// #112 (A10): Trägt die Datei ein `scenario_flow`-Blatt? Grundlage ist der KI-/Heuristik-Befund
// aus dem Upload (`detected_type`) — derselbe Wert, aus dem der Server seine Vorbelegung ableitet.
// Korrigiert der Mensch den Blatt-Typ im nächsten Schritt, entscheidet beim Commit ohnehin der
// BESTÄTIGTE Typ (loadMappedSheets → draft.has_scenario_flow), nicht diese Anzeige-Heuristik.
// #125: Nach der Quellwahl kann der Typ schon aus dem Quell-Import bestätigt sein (Vorbelegung) —
// dann zählt der, sonst der KI-Befund. Sonst bliebe ein übernommener Stream unsichtbar.
const hasScenarioFlow = (sheets: ImportSheet[]): boolean =>
  sheets.some((s) => (s.confirmed_type ?? s.detected_type) === 'scenario_flow');

// #112 (A10): Führt die Datei eine „Fachbereich"-Spalte, wird das Teilprojekt JE ZEILE gelesen —
// dann wäre ein job-weites Eingabefeld irreführend (es griffe nur für Zeilen mit leerer Zelle).
// Der Schritt zeigt stattdessen einen Hinweis. Erkannt wird über die Blatt-Überschriften; welche
// Spalte am Ende wirklich auf `fachbereich` gemappt ist, entscheidet der Mapping-Schritt.
const hasFachbereichColumn = (sheets: ImportSheet[]): boolean =>
  sheets.some((s) => s.headers.some((h) => h.trim().toLowerCase().startsWith('fachbereich')));

// #125 (Vorbelegung): Nach dem Setzen der Quelle liefert der Server die Blätter mit den
// übernommenen Werten zurück. Die Zeilenzahlen kennt nur die Upload-Antwort — sie bleiben.
function mergeSheets(local: ImportSheet[], server: ImportSheet[] | undefined): ImportSheet[] {
  if (!server) return local;
  return local.map((l) => {
    const s = server.find((x) => x.sheet_id === l.sheet_id);
    if (!s) return l;
    const merged: ImportSheet = { ...l, ...s };
    if (l.row_count !== undefined) merged.row_count = l.row_count;
    if (l.discard_count !== undefined) merged.discard_count = l.discard_count;
    return merged;
  });
}

// #73 (Task 7): Die Blatt-Erkennung läuft nach dem Upload im Hintergrund. Solange sie läuft, zeigt der
// Schritt nur ihren Fortschritt — die Struktur-Vorbelegung entsteht erst mit den Befunden (#112), und
// die API sperrt alle Eingaben bis dahin (409 import_classification_pending).
//
// Der erste „Start" ist die Upload-Antwort selbst (die Erkennung läuft schon); erst ein Klick auf
// „Neu starten" ruft restartClassification. Das Flag statt eines einmal verbrauchten Werts hält auch,
// wenn React den Effekt doppelt ausführt (StrictMode): ein zweiter Start nach fertiger Erkennung
// bekäme sonst 409 import_already_classified.
function ClassificationProgress({
  projectId,
  jobId,
  initial,
  onDone,
}: {
  projectId: string;
  jobId: string;
  initial: ImportComputation;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const restartRequested = useRef(false);
  const { computation, error, restart } = useImportComputation({
    start: async () => {
      if (!restartRequested.current) return initial;
      try {
        return await importApi.restartClassification(projectId, jobId);
      } catch (err) {
        // #73: Die Erkennung kann zwischen zwei Abfragen fertig geworden sein (Abfrage scheiterte,
        // „Neu starten" kommt zu spät) — dann den tatsächlichen Stand laden statt Fehler zu zeigen.
        if (err instanceof Error && err.message === 'import_already_classified') {
          return importApi.getComputation(projectId, jobId);
        }
        throw err;
      }
    },
    poll: () => importApi.getComputation(projectId, jobId),
    onDone,
  });
  const restartNow = (): void => {
    restartRequested.current = true;
    restart();
  };

  if (error) {
    return (
      <div className="max-w-xl">
        <p role="alert" className="text-xs text-status-fail">
          {translateImportError(t, error)}
        </p>
        <button
          type="button"
          onClick={restartNow}
          className="mt-4 rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white"
        >
          {t('import.computation.restart')}
        </button>
      </div>
    );
  }
  if (!computation) return null;
  return (
    <ComputationStatus computation={computation} kind="classification" onRestart={restartNow} />
  );
}

export function UploadStep({
  projectId,
  onUploaded,
}: {
  projectId: string;
  onUploaded: (jobId: string, sheets: ImportSheet[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedState | null>(null);
  // #73: Upload-Antwort, solange die Blatt-Erkennung läuft. Ihre Blätter tragen die Zeilenzahlen,
  // die GET .../jobs/:id nicht kennt.
  const [classifying, setClassifying] = useState<UploadResult | null>(null);
  // Fix-Runde 1 (#125): solange die Quellwahl gespeichert wird, ist der Stand der Blätter offen —
  // „Weiter" gäbe sonst den alten Stand an den Klassifikations-Schritt.
  const [savingSource, setSavingSource] = useState(false);
  // #96: ruhiges, feldnahes "gespeichert"-Feedback (Muster aus Task 9, scope-table.tsx) — bisher
  // gab es nach einem erfolgreichen Struktur-Blur gar kein Erfolgssignal.
  const { savedId, flash } = useSavedFlash();

  async function handleFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      setClassifying(await importApi.upload(projectId, file));
    } catch (err) {
      setError(translateImportError(t, err instanceof Error ? err.message : 'upload_failed'));
    } finally {
      setBusy(false);
    }
  }

  // #73: Erkennung fertig → den erkannten Stand lesen (Befunde, Struktur-Vorbelegung) und wie bisher
  // weitermachen. Scheitert das Lesen, geht es zurück zur Dateiauswahl, mit Fehler.
  async function finishClassification(upload: UploadResult): Promise<void> {
    try {
      const { job, sheets } = await importApi.getJob(projectId, upload.job.import_job_id);
      await continueAfterClassification(job, mergeSheets(upload.sheets, sheets));
    } catch (err) {
      setError(translateImportError(t, err instanceof Error ? err.message : 'upload_failed'));
    } finally {
      setClassifying(null);
    }
  }

  async function continueAfterClassification(job: ImportJob, sheets: ImportSheet[]): Promise<void> {
    // #125 (Q5): Kandidaten laden und den Vorschlag SOFORT speichern — er ist sichtbar
    // vorausgewählt und jederzeit auf „Neuanlage" umstellbar (HITL). Scheitert Laden oder
    // Speichern, bleibt der Import eine Neuanlage wie bisher; die Auswahl zeigt dann auch
    // „Neuanlage" (nie einen Stand, den der Server nicht hat), der Fehler steht im Alert.
    let candidates: ReimportCandidate[] = [];
    let current = sheets;
    let streamName = job.stream_name ?? '';
    let teilprojektName = job.teilprojekt_name ?? '';
    let structurePrefill: StructurePrefill | null = null;
    let reimportOf = '';
    let suggested = false;
    try {
      const r = await importApi.getReimportCandidates(projectId, job.import_job_id);
      candidates = r.candidates;
      if (r.suggested_job_id) {
        const saved = await importApi.setStructure(projectId, job.import_job_id, {
          reimport_of_job_id: r.suggested_job_id,
        });
        current = mergeSheets(sheets, saved.sheets);
        if (saved.job) {
          streamName = saved.job.stream_name ?? '';
          teilprojektName = saved.job.teilprojekt_name ?? '';
        }
        structurePrefill = saved.structure_prefill ?? null;
        reimportOf = r.suggested_job_id;
        suggested = true;
      }
    } catch (err) {
      setError(
        translateImportError(t, err instanceof Error ? err.message : 'reimport_candidates_failed'),
      );
    }
    setUploaded({
      jobId: job.import_job_id,
      filename: job.filename,
      sheets: current,
      streamName,
      teilprojektName,
      candidates,
      reimportOf,
      suggested,
      structurePrefill,
      savedStream: streamName,
      savedTeilprojekt: teilprojektName,
      edited: [],
    });
  }

  async function persistStructure(
    field: string,
    patch: {
      stream_name?: string;
      teilprojekt_name?: string | null;
      reimport_of_job_id?: string | null;
      edited_fields?: Array<'stream_name' | 'teilprojekt_name'>;
    },
  ): Promise<StructureSaved | null> {
    if (!uploaded) return null;
    try {
      const saved = await importApi.setStructure(projectId, uploaded.jobId, patch);
      flash(field);
      return saved;
    } catch (err) {
      setError(
        translateImportError(t, err instanceof Error ? err.message : 'structure_update_failed'),
      );
      return null;
    }
  }

  // #125 (Q5): optimistisch umstellen, bei Ablehnung zurück auf den gespeicherten Stand (Muster
  // handleLanguageChange) — die Auswahl entscheidet, ob der Commit abgleicht oder neu anlegt.
  async function handleReimportChange(value: string): Promise<void> {
    if (!uploaded) return;
    const previous = uploaded.reimportOf;
    setUploaded((u) => (u ? { ...u, reimportOf: value } : u));
    setSavingSource(true);
    const saved = await persistStructure('reimport_of_job_id', {
      reimport_of_job_id: value || null,
      ...(uploaded.edited.length > 0 ? { edited_fields: uploaded.edited } : {}),
    }).finally(() => setSavingSource(false));
    if (!saved) setUploaded((u) => (u ? { ...u, reimportOf: previous } : u));
    // #125: Quellwechsel kann Blätter, Stream und Teilprojekt neu vorbelegen — Formular und
    // Klassifikations-Schritt zeigen danach den Stand des Servers.
    else
      setUploaded((u) =>
        u
          ? {
              ...u,
              sheets: mergeSheets(u.sheets, saved.sheets),
              ...(saved.job
                ? {
                    streamName: saved.job.stream_name ?? '',
                    teilprojektName: saved.job.teilprojekt_name ?? '',
                    savedStream: saved.job.stream_name ?? '',
                    savedTeilprojekt: saved.job.teilprojekt_name ?? '',
                  }
                : {}),
              structurePrefill: saved.structure_prefill ?? null,
            }
          : u,
      );
  }

  // Fix-Runde 1 (#125): nur Geändertes speichern. Ein leerer Stream ist kein speicherbarer Wert
  // (die API verlangt mindestens ein Zeichen) — er bleibt beim zuletzt gespeicherten Stand.
  // #152: Rückgabe true = gespeichert oder nichts zu tun, false = Speichern gescheitert — „Weiter"
  // gibt nur bei true weiter. Beim blur bleibt das Verhalten unverändert (Rückgabe ungenutzt).
  async function blurStream(): Promise<boolean> {
    if (!uploaded) return true;
    const value = uploaded.streamName;
    // Fix-Runde 2: geleert → zurück auf den gespeicherten Wert, sonst zeigte das Feld etwas anderes,
    // als der Server hat.
    if (value.trim() === '') {
      setUploaded((u) => (u ? { ...u, streamName: u.savedStream } : u));
      return true;
    }
    if (value === uploaded.savedStream) return true;
    if (await persistStructure('stream_name', { stream_name: value })) {
      setUploaded((u) => (u ? { ...u, savedStream: value } : u));
      return true;
    }
    return false;
  }

  async function blurTeilprojekt(): Promise<boolean> {
    if (!uploaded) return true;
    const value = uploaded.teilprojektName.trim();
    if (value === uploaded.savedTeilprojekt.trim()) return true;
    if (await persistStructure('teilprojekt_name', { teilprojekt_name: value || null })) {
      setUploaded((u) => (u ? { ...u, savedTeilprojekt: value } : u));
      return true;
    }
    return false;
  }

  // #152: „Weiter" speichert selbst, statt sich auf den blur zu verlassen — der feuert nicht
  // garantiert (Klick direkt nach dem Tippen, Enter), und so blieb das Teilprojekt eines fremden
  // Imports stehen, obwohl der Admin ein anderes getippt hatte. Nur die Felder, die der Schritt
  // auch zeigt; scheitert das Speichern, bleibt der Schritt mit dem Fehler stehen.
  async function handleContinue(): Promise<void> {
    if (!uploaded) return;
    setSavingSource(true);
    try {
      const streamOk = !hasScenarioFlow(uploaded.sheets) || (await blurStream());
      const ok = streamOk && (hasFachbereichColumn(uploaded.sheets) || (await blurTeilprojekt()));
      if (ok) onUploaded(uploaded.jobId, uploaded.sheets);
    } finally {
      setSavingSource(false);
    }
  }

  // Folgeauftrag #125: eigene Eingabe → das Feld trägt nicht mehr den Wert der Quelle.
  function editStructure(field: 'stream_name' | 'teilprojekt_name', value: string): void {
    setUploaded((u) => {
      if (!u) return u;
      const prefill = u.structurePrefill ? { ...u.structurePrefill, [field]: false } : null;
      const edited = u.edited.includes(field) ? u.edited : [...u.edited, field];
      return field === 'stream_name'
        ? { ...u, streamName: value, structurePrefill: prefill, edited }
        : { ...u, teilprojektName: value, structurePrefill: prefill, edited };
    });
  }

  if (uploaded) {
    const showStream = hasScenarioFlow(uploaded.sheets);
    const fachbereichPerRow = hasFachbereichColumn(uploaded.sheets);
    const families = groupByFamily(uploaded.candidates);
    // #152: Quelle mit anderem Dateistamm als der Upload — Stream, Teilprojekt und Spaltenzuordnung
    // kommen dann aus einer fremden Datei. Warnung statt Verbot (der Admin kann es so wollen).
    const chosen = uploaded.candidates.find((c) => c.import_job_id === uploaded.reimportOf);
    const foreignSource =
      chosen && !isSameFileStem(uploaded.filename, chosen.filename) ? chosen : null;
    const prefilledHint = (field: 'stream_name' | 'teilprojekt_name') =>
      uploaded.structurePrefill?.[field] ? (
        <span className={foreignSource ? 'text-status-warn' : 'text-status-pass'}>
          {t('import.structure.prefilled', {
            filename: uploaded.structurePrefill.source_filename,
          })}
        </span>
      ) : null;
    return (
      // key: ohne einen eigenen Key reused React sonst das <input type="file"> aus dem
      // Datei-Auswahl-Zweig für das Stream-<input type="text"> hier (gleiche Position im Baum,
      // beide Male ein <label>+<input>) — mit sichtbarem Symptom "Datei-Pfad C:\fakepath\..."
      // statt des Stream-Defaults. Der Key erzwingt einen echten Unmount/Mount statt Reuse.
      <div
        key="structure"
        className="max-w-xl rounded-card border border-[var(--border)] bg-bg-surface p-6"
      >
        {uploaded.candidates.length > 0 && (
          <>
            <label htmlFor="import-reimport-source" className="mb-2 block text-sm text-text-body">
              {t('import.reimport.source')}
            </label>
            <select
              id="import-reimport-source"
              value={uploaded.reimportOf}
              onChange={(e) => void handleReimportChange(e.target.value)}
              className="mb-1 block w-full rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-sm text-text-body"
            >
              <option value="">{t('import.reimport.none')}</option>
              {families.map(({ family, members }) => {
                const options = members.map((cand) => {
                  // Datum in der Sprache der Oberfläche, nicht der des Browsers.
                  const date = new Date(cand.committed_at).toLocaleDateString(i18n.language);
                  return (
                    <option key={cand.import_job_id} value={cand.import_job_id}>
                      {family && cand.test_cases === 0
                        ? t('import.reimport.optionSuperseded', { filename: cand.filename, date })
                        : t('import.reimport.option', {
                            filename: cand.filename,
                            date,
                            count: cand.test_cases,
                          })}
                    </option>
                  );
                });
                return family ? (
                  <optgroup
                    key={family.import_job_id}
                    label={t('import.reimport.family', { filename: family.filename })}
                  >
                    {options}
                  </optgroup>
                ) : (
                  options
                );
              })}
            </select>
            {foreignSource && (
              // <output> trägt implizit role="status" (Muster scope-table.tsx; ein <p role="status">
              // verbietet Biome a11y/useSemanticElements).
              <output
                data-testid="reimport-foreign-source"
                className="mb-1 block text-[11px] text-status-warn"
              >
                {t('import.reimport.foreignSource', { filename: foreignSource.filename })}
              </output>
            )}
            <p className="mb-4 flex flex-wrap items-center gap-x-2 text-[11px] text-text-muted">
              <span>{t('import.reimport.sourceHint')}</span>
              {uploaded.suggested && <span>{t('import.reimport.sourceSuggested')}</span>}
              {families.some((f) => f.family) && <span>{t('import.reimport.familyHint')}</span>}
              <SavedHint visible={savedId === 'reimport_of_job_id'} />
            </p>
          </>
        )}

        {showStream && (
          <>
            <label htmlFor="import-structure-stream" className="mb-2 block text-sm text-text-body">
              {t('import.structure.stream')}
            </label>
            <input
              id="import-structure-stream"
              type="text"
              value={uploaded.streamName}
              onChange={(e) => editStructure('stream_name', e.target.value)}
              onBlur={() => void blurStream()}
              className="mb-1 block w-full rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-sm text-text-body"
            />
            <p className="mb-4 flex items-center gap-2 text-[11px] text-text-muted">
              {/* Fix-Runde 1: nur der zutreffende Hinweis — übernommen ODER Dateiname. */}
              {prefilledHint('stream_name') ?? t('import.structure.streamHint')}
              <SavedHint visible={savedId === 'stream_name'} />
            </p>
          </>
        )}

        <div className="mb-2 block text-sm text-text-body">
          {/* Das <label> gehört nur zum Eingabefeld; im Fachbereich-Fall gibt es keins, dann ist
              das hier eine reine Überschrift. */}
          {fachbereichPerRow ? (
            t('import.structure.teilprojekt')
          ) : (
            <label htmlFor="import-structure-teilprojekt">
              {t('import.structure.teilprojekt')}
            </label>
          )}
        </div>
        {fachbereichPerRow ? (
          <p className="mb-4 text-[11px] text-text-muted">
            {t('import.structure.fachbereichDetected')}
          </p>
        ) : (
          <>
            <input
              id="import-structure-teilprojekt"
              type="text"
              value={uploaded.teilprojektName}
              onChange={(e) => editStructure('teilprojekt_name', e.target.value)}
              onBlur={() => void blurTeilprojekt()}
              className="mb-1 block w-full rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-sm text-text-body"
            />
            <p className="mb-4 flex items-center gap-2 text-[11px] text-text-muted">
              {prefilledHint('teilprojekt_name') ?? t('import.structure.teilprojektHint')}
              <SavedHint visible={savedId === 'teilprojekt_name'} />
            </p>
          </>
        )}

        {error && (
          <p role="alert" className="mb-3 text-xs text-status-fail">
            {error}
          </p>
        )}
        <button
          type="button"
          disabled={savingSource}
          onClick={() => void handleContinue()}
          className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('import.upload.continue')}
        </button>
      </div>
    );
  }

  if (classifying) {
    return (
      <div
        key="classifying"
        className="max-w-xl rounded-card border border-[var(--border)] bg-bg-surface p-6"
      >
        <p className="mb-3 font-mono text-sm text-text-primary">{classifying.job.filename}</p>
        <ClassificationProgress
          projectId={projectId}
          jobId={classifying.job.import_job_id}
          initial={classifying.computation}
          onDone={() => void finishClassification(classifying)}
        />
      </div>
    );
  }

  return (
    <div
      key="file-picker"
      className="max-w-xl rounded-card border border-[var(--border)] bg-bg-surface p-6"
    >
      <label htmlFor="import-file" className="mb-2 block text-sm text-text-body">
        {t('import.upload.chooseFile')}
      </label>
      <input
        id="import-file"
        type="file"
        accept=".xlsx"
        disabled={busy}
        onChange={(e) => void handleFile(e.target.files?.[0])}
        className="block w-full text-sm text-text-body"
      />
      <p className="mt-2 text-[11px] text-text-muted">{t('import.upload.hint')}</p>
      {error && (
        <p role="alert" className="mt-3 text-xs text-status-fail">
          {error}
        </p>
      )}
    </div>
  );
}

export function ClassifyStep({
  projectId,
  jobId,
  sheets,
  onConfirmed,
}: {
  projectId: string;
  jobId: string;
  sheets: ImportSheet[];
  onConfirmed: (sheets: ImportSheet[]) => void;
}) {
  const { t } = useTranslation();
  const [types, setTypes] = useState<Record<string, SheetType>>(() =>
    Object.fromEntries(
      sheets.map((s) => [
        s.sheet_id,
        (s.confirmed_type ?? s.detected_type ?? 'freeform') as SheetType,
      ]),
    ),
  );
  // Task 11 (#67): "Blatt ignorieren" — eigener Zustand neben types, weil der Ausschluss
  // unabhängig vom Blatt-Typ ist und sofort persistiert (nicht erst beim Weiter-Klick wie types).
  const [excluded, setExcluded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sheets.map((s) => [s.sheet_id, s.excluded ?? false])),
  );
  // #72: Quellsprache — wie excluded ein eigener, sofort persistierender Zustand, unabhängig vom
  // Typ-Bestätigungs-Schritt. '' = "kein Vorschlag" (detected_language unbekannt/null); der Mensch
  // muss trotzdem wählen können, ohne dass ein Vorschlag vorbelegt wäre.
  const [language, setLanguage] = useState<Record<string, 'de' | 'en' | ''>>(() =>
    Object.fromEntries(
      sheets.map((s) => [
        s.sheet_id,
        s.detected_language === 'en' ? 'en' : s.detected_language === 'de' ? 'de' : '',
      ]),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // #75: rollt den optimistischen Zustand bei einem PATCH-Fehler zurück (Muster aus
  // handleLanguageChange unten) — sonst zeigt die Checkbox einen Stand, den der Server nie
  // übernommen hat, und Anzeige/Server laufen auseinander.
  async function handleExcludeToggle(sheetId: string, next: boolean): Promise<void> {
    const previous = excluded[sheetId] ?? false;
    setExcluded((prev) => ({ ...prev, [sheetId]: next }));
    try {
      await importApi.setSheetExcluded(projectId, jobId, sheetId, next);
    } catch (err) {
      setExcluded((prev) => ({ ...prev, [sheetId]: previous }));
      setError(
        translateImportError(t, err instanceof Error ? err.message : 'exclude_update_failed'),
      );
    }
  }

  // #72: rollt den optimistischen Zustand bei einem PATCH-Fehler zurück (gleiches Muster wie
  // handleExcludeToggle oben) — sonst zeigt die UI eine Sprache an, die der Server nie übernommen
  // hat, und der Draft-Verwurf (Server) und die Anzeige (Client) laufen auseinander.
  async function handleLanguageChange(sheetId: string, next: 'de' | 'en'): Promise<void> {
    const previous = language[sheetId] ?? '';
    setLanguage((prev) => ({ ...prev, [sheetId]: next }));
    try {
      await importApi.setSheetLanguage(projectId, jobId, sheetId, next);
    } catch (err) {
      setLanguage((prev) => ({ ...prev, [sheetId]: previous }));
      setError(
        translateImportError(t, err instanceof Error ? err.message : 'language_update_failed'),
      );
    }
  }

  async function handleContinue(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Jedes Blatt wird bestätigt — auch das unveränderte: die Bestätigung IST der HITL-Schritt
      // und die Antwort liefert den Mapping-Vorschlag für Task 19.
      for (const s of sheets) {
        await importApi.confirmSheetType(projectId, jobId, s.sheet_id, types[s.sheet_id]!);
      }
      onConfirmed(
        sheets.map((s) => ({
          ...s,
          confirmed_type: types[s.sheet_id]!,
          excluded: excluded[s.sheet_id] ?? s.excluded ?? false,
        })),
      );
    } catch (err) {
      setError(translateImportError(t, err instanceof Error ? err.message : 'confirm_failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {sheets.map((s) => (
        <div
          key={s.sheet_id}
          className="rounded-card border border-[var(--border)] bg-bg-surface p-4"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm text-text-primary">{s.sheet_name}</span>
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {t('import.classify.rowCounts', {
                rows: s.row_count ?? 0,
                discarded: s.discard_count ?? 0,
              })}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            {/* AiChip-Muster (Design-Spec §4): blaues Tint, "✦" — KI-Befund, nie stille Änderung. */}
            <span className="rounded-chip bg-[var(--tint-blue)] px-2 py-1 text-[11px] text-text-body">
              {s.classification_source === 'ai'
                ? t('import.classify.aiVerdict', {
                    type: t(`import.sheetTypes.${s.detected_type ?? 'freeform'}`),
                    confidence: s.confidence ?? 0,
                  })
                : t('import.classify.heuristicVerdict', {
                    type: t(`import.sheetTypes.${s.detected_type ?? 'freeform'}`),
                  })}
            </span>
            <label htmlFor={`type-${s.sheet_id}`} className="sr-only">
              {t('import.classify.typeFor', { sheet: s.sheet_name })}
            </label>
            <select
              id={`type-${s.sheet_id}`}
              aria-label={t('import.classify.typeFor', { sheet: s.sheet_name })}
              value={types[s.sheet_id]}
              onChange={(e) => setTypes({ ...types, [s.sheet_id]: e.target.value as SheetType })}
              className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
            >
              {SHEET_TYPES.map((st) => (
                <option key={st} value={st}>
                  {t(`import.sheetTypes.${st}`)}
                </option>
              ))}
            </select>
            {/* Task 11 (#67): unabhängig vom Typ-Select — ein Meta-Blatt (z. B. "Process
                Overview") soll den Import gar nicht erst erreichen, egal welcher Typ zufällig
                erkannt/gewählt wurde. Persistiert sofort (nicht erst beim Weiter-Klick). Der
                Blattname steckt im Label-Text selbst (wie typeFor) statt in einem separaten
                aria-label — sonst würden wrapping-Label-Text und aria-label auseinanderlaufen. */}
            <label
              htmlFor={`exclude-${s.sheet_id}`}
              className="flex items-center gap-1 text-[11px] text-text-muted"
            >
              <input
                id={`exclude-${s.sheet_id}`}
                type="checkbox"
                checked={excluded[s.sheet_id] ?? false}
                onChange={(e) => void handleExcludeToggle(s.sheet_id, e.target.checked)}
              />
              {t('import.classify.excludeSheet', { sheet: s.sheet_name })}
            </label>
          </div>
          {/* #125 (Vorbelegung): woher Typ, Sprache, Ignorieren und Mapping kommen. Der Hinweis
              gilt nur, solange der Admin hier nichts geändert hat — danach stimmt er nicht mehr. */}
          {s.prefill?.status === 'applied' &&
            types[s.sheet_id] === (s.confirmed_type ?? s.detected_type ?? 'freeform') &&
            (excluded[s.sheet_id] ?? false) === (s.excluded ?? false) &&
            (language[s.sheet_id] ?? '') === (s.detected_language ?? '') && (
              <p className="mt-2 text-[11px] text-status-pass">
                {t('import.classify.prefillApplied', { filename: s.prefill.source_filename })}
              </p>
            )}
          {/* Fix-Runde 1: Werte eines früheren Imports — die gewählte Quelle hat das Blatt nicht,
              führt andere Spalten, oder es ist keine gewählt. Nicht „nichts übernommen" sagen,
              während sichtbar Werte von dort dastehen. */}
          {s.prefill?.status === 'from_other_import' && (
            <p className="mt-2 text-[11px] text-status-warn">
              {t('import.classify.prefillFromOtherImport', {
                filename: s.prefill.source_filename,
              })}
            </p>
          )}
          {/* Fix-Runde 2: nach „keiner" gibt es keine gewählte Quelle — eigener Text. */}
          {s.prefill?.status === 'kept_without_source' && (
            <p className="mt-2 text-[11px] text-status-warn">
              {t('import.classify.prefillKeptWithoutSource', {
                filename: s.prefill.source_filename,
              })}
            </p>
          )}
          {s.prefill?.status === 'headers_differ' && (
            <p className="mt-2 text-[11px] text-status-warn">
              {t('import.classify.prefillHeadersDiffer', { filename: s.prefill.source_filename })}
            </p>
          )}
          {/* #72: DE/EN-Umschalter — vorbelegt mit detected_language, aber immer wählbar (auch bei
              unbekannter Erkennung). Ändert sich der Wert, verwirft der Server den persistierten
              Preview-Draft (Sprache bestimmt die Übersetzungsrichtung) — der Hinweis-Text darunter
              macht das sichtbar, sonst wirkt der stille Vorschau-Verlust wie ein Fehler. */}
          <div className="mt-2 flex items-center gap-2">
            <label htmlFor={`language-${s.sheet_id}`} className="sr-only">
              {t('import.classify.languageFor', { sheet: s.sheet_name })}
            </label>
            <select
              id={`language-${s.sheet_id}`}
              aria-label={t('import.classify.languageFor', { sheet: s.sheet_name })}
              value={language[s.sheet_id] ?? ''}
              onChange={(e) => {
                const next = e.target.value;
                if (next === 'de' || next === 'en') void handleLanguageChange(s.sheet_id, next);
              }}
              className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
            >
              <option value="">{t('import.classify.languageUnknown')}</option>
              <option value="de">{t('import.classify.languageDe')}</option>
              <option value="en">{t('import.classify.languageEn')}</option>
            </select>
          </div>
          <p className="mt-1 text-[11px] text-text-muted">{t('import.classify.languageHint')}</p>
          {/* Task 9 (#64): scenario_flow is the newest, least familiar type — a short hint
              explains what the human is opting into once they pick it (row = test case,
              ID column = scenario/E2E chain). Other types are self-explanatory from the AI/
              heuristic verdict chip above, so the hint stays scoped to this one type. */}
          {types[s.sheet_id] === 'scenario_flow' && (
            <p className="mt-2 text-[11px] text-text-muted">
              {t('import.sheetTypes.scenario_flow_hint')}
            </p>
          )}
          {s.reason && <p className="mt-2 text-[11px] text-text-muted">{s.reason}</p>}
        </div>
      ))}
      {error && (
        <p role="alert" className="text-xs text-status-fail">
          {error}
        </p>
      )}
      <div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void handleContinue()}
          className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('import.classify.continue')}
        </button>
      </div>
    </div>
  );
}
